import { useState } from "react";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, CheckCircle2, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Réponse volontairement neutre (anti-énumération). On affiche toujours l'écran de confirmation.
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
    } catch (e) { /* Réponse volontairement neutre (anti-énumération) */ console.debug("forgot-password request failed (neutral)", e); }
    finally {
      setLoading(false);
      setSent(true);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="forgot-password-page">
      <Link to="/connexion" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"
            data-testid="forgot-back-login">
        <ArrowLeft size={14}/> Retour à la connexion
      </Link>
      <h1 className="mt-3 font-heading text-3xl font-extrabold">Mot de passe oublié</h1>
      <p className="mt-2 text-sm text-slate-600">
        Entrez l'email associé à votre compte. Nous vous enverrons un lien pour choisir un nouveau mot de passe.
      </p>

      {sent ? (
        <div className="mt-8 bg-white border border-emerald-200 rounded-2xl p-6" data-testid="forgot-success-panel">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 grid place-items-center shrink-0">
              <CheckCircle2 size={22}/>
            </div>
            <div className="flex-1">
              <div className="font-heading font-extrabold text-slate-900">Email envoyé</div>
              <p className="mt-1 text-sm text-slate-600">
                Si un compte PROXIreduc-28 existe pour <b>{email}</b>, vous recevrez d'ici quelques minutes un lien de réinitialisation. Il expire dans <b>30 minutes</b>.
              </p>
              <p className="mt-2 text-xs text-slate-500">
                Pensez à vérifier vos courriers indésirables. L'expéditeur est <b>PROXIreduc-28</b>.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/connexion">
                  <Button variant="outline" className="h-10" data-testid="forgot-goto-login">Retour à la connexion</Button>
                </Link>
                <Button variant="ghost" className="h-10" onClick={() => setSent(false)} data-testid="forgot-reset-form">
                  Renvoyer un email
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 space-y-4"
              data-testid="forgot-password-form">
          <div>
            <Label>Email</Label>
            <div className="mt-1 relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <Input type="email" required autoFocus value={email}
                     onChange={(e) => setEmail(e.target.value)}
                     className="pl-9"
                     placeholder="vous@exemple.fr"
                     data-testid="forgot-email-input"/>
            </div>
          </div>
          <Button type="submit" disabled={loading || !email}
                  className="w-full btn-shine text-white h-11"
                  data-testid="forgot-submit-btn">
            {loading ? "Envoi…" : "M'envoyer le lien"}
          </Button>
          <div className="text-center text-sm text-slate-500">
            Vous vous souvenez ? <Link to="/connexion" className="text-[#E05638] font-semibold">Se connecter</Link>
          </div>
        </form>
      )}
    </div>
  );
}
